import { Vector2 } from 'three';
import type { HipSpineLandmarkApiObject } from '@/api/hip-spine-study/hipSpineLandmark';
import {
    HipSpineLandmarkName,
    selectLumbarPair,
    swapAnteriorPosteriorLandmarks,
    type HighestLumbarSelection,
    type LumbarLevel,
} from '@/domain/anatomy/hipSpineLandmarks';
import type { SideId } from '@/domain/anatomy/side';
import { DEFAULT_FEMUR_HEAD_RADIUS_FALLBACK, type EditContext } from '@/views/3d-planner/hipSpine/xrayEdit/editState';
import type { AnnotationGroup } from '@/views/3d-planner/hipSpine/types';
import { landmarkToImagePoint } from '@/views/3d-planner/hipSpine/hipSpineCoordinates';

/**
 * Effective lumbar level for a posture: the user's explicit choice made via
 * `setLumbarLevel`, otherwise the posture's AI-detected
 * `lumbar_vertebra_end_plate` — independent of per-landmark
 * detection reliability. The `?? 'L1'` is a type guard for the
 * `hipSpinePostures.find` miss (no posture loaded for this id yet); the
 * field itself is always present on the API object. L1 is the safe default.
 */
export function selectedLumbarLevel(ctx: EditContext, postureId: string): LumbarLevel {
    const explicit = ctx.lumbarLevelByPosture.get(postureId);
    if (explicit) return explicit;
    const posture = ctx.store.hipSpinePostures.find((candidate) => candidate.posture === postureId);
    return posture?.lumbar_vertebra_end_plate ?? 'L1';
}

/**
 * The posture's landmarks as currently in the scene: the API source
 * (`viewState.landmarks`) with any user edits overlaid by name, returned as a
 * new array. Non-destructive — neither the source nor the edits overlay is
 * mutated. No anterior/posterior swap or lumbar pruning applied yet.
 */
export function editedLandmarks(ctx: EditContext, postureId: string): HipSpineLandmarkApiObject[] {
    const viewState = ctx.store.viewState(postureId);
    if (!viewState) return [];
    const edits = ctx.editsByPosture.get(postureId);
    if (!edits) return viewState.landmarks;
    return viewState.landmarks.map((landmark) => {
        const edit = edits.landmarks[landmark.name];
        return edit
            ? { ...landmark, current_x: edit.current_x, current_y: edit.current_y }
            : landmark;
    });
}

/**
 * The displayed lumbar selection: the edited (scene) landmarks, with their
 * anterior↔posterior designation swapped when the viewed side differs from
 * native (the image itself never flips), pruned to the selected lumbar level
 * (defaults to the posture's `lumbar_vertebra_end_plate`).
 */
export function displaySelection(ctx: EditContext, postureId: string): HighestLumbarSelection {
    const viewState = ctx.store.viewState(postureId);
    if (!viewState) return { pair: null, landmarks: [] };
    const edited = editedLandmarks(ctx, postureId);
    const oriented = isSwapped(ctx, postureId) ? swapAnteriorPosteriorLandmarks(edited) : edited;
    return selectLumbarPair(oriented, selectedLumbarLevel(ctx, postureId));
}

export function resolvedLandmarks(ctx: EditContext, postureId: string): HipSpineLandmarkApiObject[] {
    return displaySelection(ctx, postureId).landmarks;
}

/**
 * World-space (image-pixel) centre of a hip-joint-centre landmark for the given
 * head, resolved through any in-progress edits. Returns null when the image or
 * the HJC landmark is unavailable.
 */
export function hjcWorld(ctx: EditContext, postureId: string, head: 1 | 2): Vector2 | null {
    const viewState = ctx.store.viewState(postureId);
    if (!viewState?.imageSize) return null;
    const hjcName = head === 1 ? HipSpineLandmarkName.HJC1 : HipSpineLandmarkName.HJC2;
    const lm = resolvedLandmarks(ctx, postureId).find((candidate) => candidate.name === hjcName);
    if (!lm) return null;
    return landmarkToImagePoint(lm, viewState.imageSize);
}

// Returns the stored radius: a fraction of image width (normalized, like the coordinates).
// Scene/hit-test callers must multiply by `imageSize.width`; the save plan persists it as-is.
export function resolvedFemurRadius(ctx: EditContext, postureId: string, head: 1 | 2): number {
    const edits = ctx.editsByPosture.get(postureId);
    if (edits?.femurRadius[head] !== undefined) return edits.femurRadius[head]!;
    const posture = ctx.store.hipSpinePostures.find((candidate) => candidate.posture === postureId);
    const apiValue = head === 1 ? posture?.femur_1_head_radius : posture?.femur_2_head_radius;
    return apiValue ?? DEFAULT_FEMUR_HEAD_RADIUS_FALLBACK;
}

/**
 * Native (un-swapped) side the x-ray is viewed from, implied by the
 * posture's `side`. The View renders this as the "Anterior-{Side}" label.
 */
export function nativeSide(ctx: EditContext, postureId: string): SideId {
    const posture = ctx.store.hipSpinePostures.find((candidate) => candidate.posture === postureId);
    return posture?.side === 'right' ? 'right' : 'left';
}

/**
 * Effective viewed side: the user's explicit toggle choice, otherwise the
 * posture's native side.
 */
export function viewedSide(ctx: EditContext, postureId: string): SideId {
    return ctx.viewedSideByPosture.get(postureId) ?? nativeSide(ctx, postureId);
}

/** True when the viewed side differs from the posture's native one. */
export function isSwapped(ctx: EditContext, postureId: string): boolean {
    return viewedSide(ctx, postureId) !== nativeSide(ctx, postureId);
}

/**
 * Stable visibility key for an annotation group. The lumbar section is a
 * single, level-independent row whose label tracks the selected end plate
 * (L1…L5), so it keys by category; every other section keys by its stable
 * label (PF1, PF2, SE, APP). Keying this way lets a hidden lumbar row stay
 * hidden across a level change, and a hidden femoral group stay hidden
 * across side toggles and landmark drags — all of which rebuild the groups.
 */
export function groupVisibilityKey(group: AnnotationGroup): string {
    return group.category === 'lumbar' ? 'lumbar' : group.label;
}

export function isGroupHidden(ctx: EditContext, postureId: string, group: AnnotationGroup): boolean {
    return ctx.hiddenGroupKeysByPosture.get(postureId)?.has(groupVisibilityKey(group)) ?? false;
}

/**
 * The hidden group keys for a posture, as an array — a stable, watchable
 * projection of the intent. The scene subscribes to this to re-render
 * annotation visibility when the surgeon hides or shows a group.
 */
export function hiddenGroupKeys(ctx: EditContext, postureId: string): string[] {
    return [...(ctx.hiddenGroupKeysByPosture.get(postureId) ?? [])];
}

/**
 * World-space (image-pixel) point the loupe camera should centre on for the
 * current drag: the dragged landmark's position, or the femur-circle edge point
 * in the cursor's direction from the HJC centre. Null when no drag is active.
 */
export function loupeTargetWorld(ctx: EditContext): Vector2 | null {
    const drag = ctx.dragState.value;
    if (drag.kind === 'idle') return null;
    const viewState = ctx.store.viewState(drag.postureId);
    if (!viewState?.imageSize) return null;
    const { width } = viewState.imageSize;
    if (drag.kind === 'landmark') {
        const lm = resolvedLandmarks(ctx, drag.postureId).find(
            (candidate) => candidate.name === drag.landmarkName,
        );
        return lm ? landmarkToImagePoint(lm, viewState.imageSize) : null;
    }
    const center = hjcWorld(ctx, drag.postureId, drag.head);
    if (!center) return null;
    const radiusPx = resolvedFemurRadius(ctx, drag.postureId, drag.head) * width;
    const dir = drag.pointerWorld.clone().sub(center);
    if (dir.lengthSq() < 1e-9) return center.clone();
    return center.add(dir.normalize().multiplyScalar(radiusPx));
}

/**
 * Wire the resolution helpers over a shared {@link EditContext}. Returns the
 * public derived-state surface the ViewModel exposes (internal helpers
 * `nativeSide` / `groupVisibilityKey` are used by the actions but not exposed).
 */
export function createEditResolution(ctx: EditContext) {
    return {
        selectedLumbarLevel: (postureId: string) => selectedLumbarLevel(ctx, postureId),
        editedLandmarks: (postureId: string) => editedLandmarks(ctx, postureId),
        displaySelection: (postureId: string) => displaySelection(ctx, postureId),
        resolvedLandmarks: (postureId: string) => resolvedLandmarks(ctx, postureId),
        resolvedFemurRadius: (postureId: string, head: 1 | 2) =>
            resolvedFemurRadius(ctx, postureId, head),
        viewedSide: (postureId: string) => viewedSide(ctx, postureId),
        isSwapped: (postureId: string) => isSwapped(ctx, postureId),
        isGroupHidden: (postureId: string, group: AnnotationGroup) =>
            isGroupHidden(ctx, postureId, group),
        hiddenGroupKeys: (postureId: string) => hiddenGroupKeys(ctx, postureId),
        loupeTargetWorld: () => loupeTargetWorld(ctx),
    };
}
